import { getApiBaseUrl, handleApiError } from '../../core/config/api';
import { GameSettings } from '../../features/game/dashboard/components/SettingsModal';

class GameSettingsService {
  private getAuthToken(): string | null {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    return token;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return response;
  }

  /**
   * Charge les paramètres du joueur depuis le serveur
   */
  async getSettings(): Promise<{ success: boolean; data?: GameSettings; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/settings`);
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Sauvegarde les paramètres du joueur sur le serveur
   */
  async saveSettings(settings: GameSettings): Promise<{ success: boolean; data?: GameSettings; error?: string }> {
    try {
      const apiUrl = getApiBaseUrl(true);
      const response = await this.fetchWithAuth(`${apiUrl}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      return data;
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Met à jour partiellement les paramètres du joueur
   */
  async updateSettings(partialSettings: Partial<GameSettings>): Promise<{ success: boolean; data?: GameSettings; error?: string }> {
    try {
      // Charger les paramètres actuels
      const currentSettingsResult = await this.getSettings();
      if (!currentSettingsResult.success || !currentSettingsResult.data) {
        return { success: false, error: 'Impossible de charger les paramètres actuels' };
      }

      // Fusionner avec les nouveaux paramètres
      const updatedSettings = { ...currentSettingsResult.data, ...partialSettings };

      // Sauvegarder
      return await this.saveSettings(updatedSettings);
    } catch (error: any) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

const gameSettingsService = new GameSettingsService();
export default gameSettingsService;

